import React, { useState, useEffect } from "react";
import { Label, TextInput, Radio } from "flowbite-react";
import { HiPlus, HiMinus, HiChevronDown, HiChevronUp } from "react-icons/hi";
import { useTranslation } from "@repo/core";
import type { Strategy } from "@repo/api";
import { useSettings } from "../../../hooks/indicators/useSettings";
import { PeriodSelector, type Period } from "@repo/core";
import StrategySlotSelectionModal from "../overwriteModal/StrategySlotSelectionModal";
import { useStrategyDetailType } from "../../../hooks/useStrategyDetailType";
import { useStrategyRecalculateHandler } from "../../../hooks/useStrategyRecalculateHandler";
import { useRecalculatePortfolio } from "../hooks/useRecalculatePortfolio";
import { ConfirmationModal } from "../confirmationModal";
import { useAuthentication } from "@repo/core";

const StrategySettings = ({
  strategy,
  dateRange,
}: {
  strategy: Strategy;
  dateRange?: string;
}) => {
  const { t, tStrategy } = useTranslation();
  const {
    numberOfStocks,
    maxNumberOfStocks,
    initialCapital,
    backtestingPeriod,
    rankingRule,
    portfolioConstruction,
    leverage,
    setNumberOfStocks,
    setInitialCapital,
    setBacktestingPeriod,
    setRankingRule,
    setPortfolioConstruction,
    setLeverage,
    resetStrategySettings,
    initialStrategySettings,
  } = useSettings(strategy);
  const { type: pageType } = useStrategyDetailType(strategy);
  const {
    showSlotSelectionModal,
    handleSlotSelectionModalClose,
    recalculatePortfolio,
    isRecalculating,
  } = useRecalculatePortfolio({
    strategy,
    pageType,
    initialCapital,
    backtestingPeriod,
    numberOfStocks,
    portfolioConstruction,
    leverage,
    rankingRule,
    dateRange: dateRange as Period,
  });

  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
  const { user } = useAuthentication();

  // Track if any settings have changed
  const [hasChanges, setHasChanges] = useState(false);

  // Track confirmation modal state for dateRange recalculation
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");

  // Use the new recalculate handler hook
  const { pendingCloneData } = useStrategyRecalculateHandler({
    strategy,
    pageType,
    dateRange,
    hasChanges,
    initialCapital,
    backtestingPeriod,
    numberOfStocks,
    portfolioConstruction,
    leverage,
    rankingRule,
    resetStrategySettings,
  });

  // Check for changes whenever any setting changes
  useEffect(() => {
    const currentSettings = {
      numberOfStocks,
      initialCapital,
      backtestingPeriod,
      rankingRule,
      portfolioConstruction,
      leverage,
    };

    const hasAnyChange = Object.keys(initialStrategySettings).some(
      (key) =>
        initialStrategySettings[key as keyof typeof initialStrategySettings] !==
        currentSettings[key as keyof typeof currentSettings]
    );

    setHasChanges(hasAnyChange);
  }, [
    numberOfStocks,
    initialCapital,
    backtestingPeriod,
    rankingRule,
    portfolioConstruction,
    leverage,
    initialStrategySettings,
  ]);

  // strategyOverview now rendered via translations

  const incrementStocks = () => {
    if (numberOfStocks < maxNumberOfStocks) {
      setNumberOfStocks(numberOfStocks + 1);
    }
  };

  const decrementStocks = () => {
    if (numberOfStocks > 1) {
      setNumberOfStocks(numberOfStocks - 1);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    if (!isNaN(value) && value >= 1 && value <= maxNumberOfStocks) {
      setNumberOfStocks(value);
    }
  };

  const incrementLeverage = () => {
    if (leverage < 5) {
      setLeverage(Math.round((leverage + 0.1) * 10) / 10);
    }
  };

  const decrementLeverage = () => {
    if (leverage > 1) {
      setLeverage(Math.round((leverage - 0.1) * 10) / 10);
    }
  };

  const handleLeverageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value) && value >= 1 && value <= 5) {
      setLeverage(Math.round(value * 10) / 10);
    }
  };

  const handleCapitalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value.replace(/[^0-9]/g, ""));
    if (!isNaN(value) && value >= 0) {
      setInitialCapital(value);
    }
  };

  const setCapitalPreset = (amount: number) => {
    setInitialCapital(amount);
  };

  const handlePeriodChange = (period: Period) => {
    setBacktestingPeriod(period);
  };

  return (
    <>
      <div className="flex flex-col" data-tour="strategy-settings">
        {/* Header and overview */}
        <div>
          {/* Strategy Overview */}
          <div className="text-sm text-gray-600 leading-relaxed mb-4">
            {tStrategy("settings.topStocks")} {numberOfStocks} {tStrategy("settings.stocks")} | {tStrategy("settings.leverageLabel")} {leverage.toFixed(1)}x |
            {portfolioConstruction === "LongOnly" && ` ${tStrategy("settings.allocation.long")} |`}{" "}
            {portfolioConstruction === "ShortOnly" && ` ${tStrategy("settings.allocation.short")} |`}{" "}
            {portfolioConstruction === "LongShort" &&
              ` ${tStrategy("settings.allocation.longShort")} | `}
            {tStrategy("settings.capital")} {initialCapital.toLocaleString()} {tStrategy("settings.usd")}
          </div>

          {/* Divider */}
          <hr className="border-gray-200 mb-6" />
        </div>

        {/* Form content */}
        <div>
          <form className="space-y-6">
            {/* Number of Stocks Input */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label
                  htmlFor="numberOfStocks"
                  className="text-sm font-medium text-gray-700"
                >
                  {tStrategy("settings.numberOfStocks")}
                </Label>
                <span className="text-xs text-gray-500">
                  {`${tStrategy("settings.maxStocks")} ${maxNumberOfStocks}`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <TextInput
                  id="numberOfStocks"
                  type="number"
                  min={1}
                  max={maxNumberOfStocks}
                  value={numberOfStocks}
                  onChange={handleInputChange}
                  className="text-center flex-1"
                />

                <div className="flex">
                  <button
                    type="button"
                    onClick={decrementStocks}
                    disabled={numberOfStocks <= 1}
                    className="w-8 h-8 p-0 flex items-center justify-center border transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 rounded-l cursor-pointer"
                    style={{
                      backgroundColor: "white",
                      borderColor: "#D1D5DB",
                    }}
                  >
                    <HiMinus size={14} />
                  </button>

                  <button
                    type="button"
                    onClick={incrementStocks}
                    disabled={numberOfStocks >= maxNumberOfStocks}
                    className="w-8 h-8 p-0 flex items-center justify-center border border-l-0 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 rounded-r cursor-pointer"
                    style={{
                      backgroundColor: "white",
                      borderColor: "#D1D5DB",
                    }}
                  >
                    <HiPlus size={14} />
                  </button>
                </div>
              </div>
            </div>

            {/* Initial Capital Input */}
            <div>
              <Label
                htmlFor="initialCapital"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {tStrategy("settings.initialCapital")}
              </Label>
              <div className="relative">
                <TextInput
                  id="initialCapital"
                  type="text"
                  value={initialCapital.toLocaleString()}
                  onChange={handleCapitalChange}
                  className="pr-8"
                  placeholder="10,000"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-sm">$</span>
                </div>
              </div>

              {/* Preset value links */}
              <div className="flex flex-wrap gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setCapitalPreset(10000)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {t("settingsUI.preset10k")}
                </button>
                <button
                  type="button"
                  onClick={() => setCapitalPreset(100000)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {t("settingsUI.preset100k")}
                </button>
                <button
                  type="button"
                  onClick={() => setCapitalPreset(500000)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {t("settingsUI.preset500k")}
                </button>
                <button
                  type="button"
                  onClick={() => setCapitalPreset(1000000)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {t("settingsUI.preset1m")}
                </button>
              </div>
            </div>

            {/* Backtesting History */}
            <PeriodSelector
              selectedPeriod={backtestingPeriod as Period}
              onPeriodChange={handlePeriodChange}
              label={tStrategy("settings.backtestingHistory")}
              periods={["10Y", "5Y", "3Y", "1Y", "YTD"]}
              translationNamespace="settings"
            />

            {/* Advanced Settings */}
            <div>
              <button
                type="button"
                onClick={() => setIsAdvancedExpanded(!isAdvancedExpanded)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                <span>{tStrategy("settings.advancedSettings")}</span>
                {isAdvancedExpanded ? (
                  <HiChevronUp size={16} />
                ) : (
                  <HiChevronDown size={16} />
                )}
              </button>

              {isAdvancedExpanded && (
                <div className="mt-4 space-y-4">
                  {/* Ranking Rules */}
                  <div>
                    <Label className="block text-sm font-medium text-gray-700 mb-3">
                      {tStrategy("settings.rankingRulesTitle")}
                    </Label>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center">
                        <Radio
                          id="high-score"
                          name="rankingRule"
                          value="HIGHEST"
                          checked={rankingRule === "HIGHEST"}
                          onChange={(e) => {
                            if (typeof setRankingRule === "function") {
                              setRankingRule(
                                e.target.value as "HIGHEST" | "LOWEST"
                              );
                            }
                          }}
                          className="mr-2"
                        />
                        <Label
                          htmlFor="high-score"
                          className="text-sm text-gray-600"
                        >
                          {tStrategy("settings.rankingRules.highScore")}
                        </Label>
                      </div>
                      <div className="flex items-center">
                        <Radio
                          id="low-score"
                          name="rankingRule"
                          value="LOWEST"
                          checked={rankingRule === "LOWEST"}
                          onChange={(e) => {
                            if (typeof setRankingRule === "function") {
                              setRankingRule(
                                e.target.value as "HIGHEST" | "LOWEST"
                              );
                            }
                          }}
                          className="mr-2"
                        />
                        <Label
                          htmlFor="low-score"
                          className="text-sm text-gray-600"
                        >
                          {tStrategy("settings.rankingRules.lowScore")}
                        </Label>
                      </div>
                    </div>
                  </div>

                  {/* Portfolio Construction */}
                  <div>
                    <Label className="block text-sm font-medium text-gray-700 mb-3">
                      {tStrategy("settings.portfolioConstruction")}
                    </Label>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Radio
                          id="long-short"
                          name="portfolioConstruction"
                          value="LongShort"
                          checked={portfolioConstruction === "LongShort"}
                          onChange={(e) =>
                            setPortfolioConstruction(
                              e.target.value as
                              | "LongOnly"
                              | "ShortOnly"
                              | "LongShort"
                            )
                          }
                          className="mr-2"
                        />
                        <Label
                          htmlFor="long-short"
                          className="text-sm text-gray-600"
                        >
                          {tStrategy("settings.portfolioTypes.longShort")}
                        </Label>
                      </div>
                      <div className="flex items-center">
                        <Radio
                          id="long"
                          name="portfolioConstruction"
                          value="LongOnly"
                          checked={portfolioConstruction === "LongOnly"}
                          onChange={(e) =>
                            setPortfolioConstruction(
                              e.target.value as
                              | "LongOnly"
                              | "ShortOnly"
                              | "LongShort"
                            )
                          }
                          className="mr-2"
                        />
                        <Label htmlFor="long" className="text-sm text-gray-600">
                          {tStrategy("settings.portfolioTypes.long")}
                        </Label>
                      </div>
                      <div className="flex items-center">
                        <Radio
                          id="short"
                          name="portfolioConstruction"
                          value="ShortOnly"
                          checked={portfolioConstruction === "ShortOnly"}
                          onChange={(e) =>
                            setPortfolioConstruction(
                              e.target.value as
                              | "LongOnly"
                              | "ShortOnly"
                              | "LongShort"
                            )
                          }
                          className="mr-2"
                        />
                        <Label
                          htmlFor="short"
                          className="text-sm text-gray-600"
                        >
                          {tStrategy("settings.portfolioTypes.short")}
                        </Label>
                      </div>
                    </div>
                  </div>

                  {/* Leverage Input */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label
                        htmlFor="leverage"
                        className="text-sm font-medium text-gray-700"
                      >
                        {tStrategy("settings.leverage")}
                      </Label>
                      <span className="text-xs text-gray-500">
                        {tStrategy("settings.maxLeverage")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <TextInput
                        id="leverage"
                        type="number"
                        min={1}
                        max={5}
                        step={0.1}
                        value={leverage.toFixed(1)}
                        onChange={handleLeverageChange}
                        className="text-center flex-1"
                      />

                      <div className="flex">
                        <button
                          type="button"
                          onClick={decrementLeverage}
                          disabled={leverage <= 1}
                          className="w-8 h-8 p-0 flex items-center justify-center border transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 rounded-l cursor-pointer"
                          style={{
                            backgroundColor: "white",
                            borderColor: "#D1D5DB",
                          }}
                        >
                          <HiMinus size={14} />
                        </button>

                        <button
                          type="button"
                          onClick={incrementLeverage}
                          disabled={leverage >= 5}
                          className="w-8 h-8 p-0 flex items-center justify-center border border-l-0 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 rounded-r cursor-pointer"
                          style={{
                            backgroundColor: "white",
                            borderColor: "#D1D5DB",
                          }}
                        >
                          <HiPlus size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Sticky Recalculate Button - always visible at bottom */}
        <div className="flex-shrink-0 sticky bottom-0 left-0 right-0 bg-white pt-4 z-10">
          <button
            type="button"
            disabled={!hasChanges || isRecalculating}
            className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 transition-all duration-200 border flex items-center justify-center gap-2 ${hasChanges && !isRecalculating
              ? "hover:shadow-md hover:scale-105 cursor-pointer"
              : "cursor-not-allowed opacity-50"
              }`}
            style={{
              backgroundColor:
                hasChanges && !isRecalculating ? "#EBF5FF" : "#F3F4F6",
              borderColor:
                hasChanges && !isRecalculating ? "#A4CAFE" : "#D1D5DB",
              color: hasChanges && !isRecalculating ? "#1A55DB" : "#9CA3AF",
            }}
            onClick={() => {
              if (hasChanges) {
                // If dateRange exists, show confirmation modal
                if (dateRange && user) {
                  const message = t(
                    "settingsUI.confirmRecalculationMessage",
                    "New draft portfolio will be created based on the updated settings and the selected date range. Do you want to continue? You can later add it to your overview."
                  );
                  setShowConfirmationModal(true);
                  setConfirmationMessage(message);
                } else if (!user) {
                  const message = t(
                    "settingsUI.confirmRecal  culationMessage",
                    "This feature is available for authenticated users. Please log in to use it."
                  );
                  setShowConfirmationModal(true);
                  setConfirmationMessage(message);
                } else {
                  // Directly call handleRecalculate for normal strategy context
                  recalculatePortfolio();
                }
              }
            }}
          >
            {isRecalculating
              ? t("settingsUI.recalculating", "Recalculating...")
              : tStrategy("settings.recalculate")}
          </button>
        </div>
      </div>

      {/* Confirmation Modal for dateRange recalculation */}
      <ConfirmationModal
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        confirmationMessage={confirmationMessage}
        isDisabled={isRecalculating || strategy.backtest.backtestInProgress}
        onContinuePress={recalculatePortfolio}
        isContinueAvailable={Boolean(user)}
      />

      {/* Strategy Slot Selection Modal */}
      <StrategySlotSelectionModal
        isOpen={showSlotSelectionModal}
        onClose={handleSlotSelectionModalClose}
        cloneData={
          pendingCloneData || { modelId: "", universe: "", period: "" }
        }
      />
    </>
  );
};

export default StrategySettings;
